import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Sidebar, Menu, MenuItem, SubMenu, sidebarClasses } from "react-pro-sidebar";
import {
  LayoutDashboard, Users, AppWindow, LogOut, SquareChartGantt, ChartSpline, X,
  NotepadTextDashed, ShieldCheck, Crown
} from 'lucide-react';
import { Img } from "components";
import Logo from '../../Assests/Images/fuelingoLogo.png';

const FONT = "'NeueMontreal', sans-serif";
const BRAND = '#EA352B';

function LogoutModal({ isOpen, onConfirm, onCancel }) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCancel}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 animate-fadeIn"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div style={{ background: 'white' }} className="relative w-full max-w-md bg-white rounded-2xl p-6 sm:p-8 shadow-2xl animate-slideUp">
          <button
            onClick={onCancel}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-[#FEE2E2] text-[#EA352B] hover:bg-red-100 hover:text-red-500 transition"
          >
            <X size={16} />
          </button>

          {/* Icon (CENTERED) */}
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#FEE2E2] flex items-center justify-center">
              <LogOut size={26} className="text-[#EA352B]" />
            </div>
          </div>

          {/* Text */}
          <div className="text-center">
            <h2 className="mb-2 text-2xl sm:text-xl text-gray-900">
              Do you want to log out?
            </h2>
            <p className="mb-6 text-sm sm:text-base text-gray-500 leading-relaxed">
              Are you sure you want to log out? Your session will be ended.
            </p>
          </div>

          <div className="flex sm:flex-row gap-3">
            <button
              onClick={onCancel}
              className="w-full py-3 rounded-lg border border-[#EA352B] text-[#EA352B] font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              style={{ backgroundColor: BRAND, color: '#fff' }}
              onClick={onConfirm}
              className="w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Super Admin profile card (logout ke neeche) ─── */
function SuperAdminCard() {
  const admin = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('adminInfo')) || {};
    } catch {
      return {};
    }
  }, []);

  const name = admin.name || admin.fullName || admin.username || 'Super Admin';
  const email = admin.email || 'admin@fuelingo.ae';
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

  return (
    <div className="sa-card">
      <div className="sa-card-glow" />
      <div className="flex items-center gap-3 relative">
        <div className="relative shrink-0">
          <div className="sa-avatar">{initials || 'SA'}</div>
          <span className="sa-crown">
            <Crown size={10} strokeWidth={2.5} />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="sa-name" title={name}>{name}</p>
          <p className="sa-email" title={email}>{email}</p>
        </div>
      </div>
      {/* <div className="sa-badge">
        <ShieldCheck size={13} strokeWidth={2.5} />
        <span>SUPER ADMIN</span>
        <span className="sa-dot" />
        <span className="sa-access">Full Access</span>
      </div> */}
    </div>
  );
}

/* ─── Main Sidebar ─── */
export default function Sidebar1() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = () => {
    localStorage.clear();
    setShowLogoutModal(false);
    navigate("/");
  };

  const menuItems = [
    { name: "Dashboard", icon: <LayoutDashboard size={18} />, path: "/admin-dashboard" },
    {
      name: "User Management",
      icon: <SquareChartGantt size={18} />,
      path: "/user-management",
      isSubMenu: true,
      children: [
        { name: "Customer", path: "/user-management/customer" },
        { name: "Service Provider", path: "/user-management/service-provider" },
      ]
    },
    { name: "Community", icon: <Users size={18} />, path: "/community" },
    {
      name: "Applications",
      icon: <AppWindow size={18} />,
      path: "/applications",
      isSubMenu: true,
      children: [
        { name: "Pending", path: "/applications/pending" },
        { name: "Approved", path: "/applications/approved" },
        { name: "Rejected", path: "/applications/rejected" },
      ]
    },
    { name: "Customer Feedbacks", icon: <NotepadTextDashed size={18} />, path: "/admin-all-feedbacks" },
    { name: "Fuel Prices", icon: <ChartSpline size={18} />, path: "/admin-fuel-prices" },
  ];

  const getActiveSubMenu = () => {
    const match = menuItems.find(
      (item) => item.isSubMenu && location.pathname.startsWith(item.path)
    );
    return match ? match.name : null;
  };

  const [openSubMenu, setOpenSubMenu] = useState(getActiveSubMenu);

  useEffect(() => {
    setOpenSubMenu(getActiveSubMenu());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      <style>
        {`
          /* Band submenu ko force hide karo (react-pro-sidebar height glitch fix) */
          .ps-submenu-root:not(.ps-open) > .ps-submenu-content {
            display: none !important;
            height: 0 !important;
            overflow: hidden !important;
            visibility: hidden !important;
          }

          /* Active top-level item ke left par red accent bar */
          .sb-main > ul > li > .ps-menu-button.ps-active,
          .sb-main > ul > li.ps-submenu-root > .ps-menu-button.ps-active {
            position: relative;
          }
          .sb-main > ul > li > .ps-menu-button.ps-active::before {
            content: '';
            position: absolute;
            left: -16px;
            top: 8px;
            bottom: 8px;
            width: 4px;
            border-radius: 0 4px 4px 0;
            background: ${BRAND};
          }

          /* Submenu children: left guide line + dot */
          .sb-main .ps-submenu-content > ul {
            position: relative;
            margin-left: 34px;
            padding: 4px 0;
            border-left: 1.5px dashed #F3C4C0;
          }
          .sb-main .ps-submenu-content .ps-menu-button::before {
            content: '';
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #D1D5DB;
            margin-right: 10px;
            flex-shrink: 0;
            transition: background 0.2s ease, transform 0.2s ease;
          }
          .sb-main .ps-submenu-content .ps-menu-button.ps-active::before,
          .sb-main .ps-submenu-content .ps-menu-button:hover::before {
            background: ${BRAND};
            transform: scale(1.3);
          }

          .sb-main .ps-menu-icon { margin-right: 6px; }
          /* index.css ka global 17px override yahan scope karke kam kiya */
          .sb-main .ps-menu-button { font-size: 15px !important; gap:10px !important;font-family: 'Inter';font-weight: 500; }
          .sb-main .ps-submenu-content .ps-menu-button { font-size: 14px !important; }

          /* Scrollable menu area with thin scrollbar */
          .sb-scroll { overflow-y: auto; scrollbar-width: thin; scrollbar-color: #E5E7EB transparent; }
          .sb-scroll::-webkit-scrollbar { width: 4px; }
          .sb-scroll::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 4px; }

          .sb-section-label {
            font-family: ${FONT};
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.12em;
            color: #9CA3AF;
            padding: 0 28px;
            margin: 8px 0 6px;
          }

          /* Logout button */
          .sb-logout {
            display: flex;
            align-items: center;
            gap: 10px;
            width: calc(100% - 32px);
            margin: 0 16px;
            padding: 10px 14px;
            border-radius: 10px;
            font-family: ${FONT};
            font-size: 15px;
            font-weight: 500;
            color: #374151;
            background: transparent;
            border: 1px solid #E5E7EB;
            transition: all 0.2s ease;
          }
          .sb-logout:hover {
            color: ${BRAND};
            background: #FEF2F2;
            border-color: #FECACA;
          }
          .sb-logout:hover .sb-logout-icon { transform: translateX(2px); }
          .sb-logout-icon { transition: transform 0.2s ease; }

          /* Super Admin card */
          .sa-card {
            position: relative;
            overflow: hidden;
            margin: 12px 16px 0;
            padding: 14px;
            border-radius: 14px;
            background: linear-gradient(120deg, rgb(27, 27, 34) 0%, rgb(42, 22, 20) 55%, rgb(234, 53, 43) 130%);
            box-shadow: 0 8px 20px -8px rgba(17, 24, 39, 0.45);
            font-family: ${FONT};
          }
          .sa-card-glow {
            position: absolute;
            top: -40px;
            right: -40px;
            width: 110px;
            height: 110px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(234,53,43,0.55) 0%, rgba(234,53,43,0) 70%);
            pointer-events: none;
          }
          .sa-avatar {
            width: 40px;
            height: 40px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 14px;
            color: #fff;
            background: linear-gradient(135deg, #F87171 0%, ${BRAND} 60%, #B91C1C 100%);
            box-shadow: 0 0 0 2px rgba(255,255,255,0.12);
          }
          .sa-crown {
            position: absolute;
            top: -6px;
            right: -6px;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #FBBF24;
            color: #78350F;
            border: 2px solid #111827;
          }
          .sa-name {
            color: #F9FAFB;
            font-size: 14px;
            font-weight: 600;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin: 0;
            font-weight:400 !important;
          }
          .sa-email {
            color: #9CA3AF;
            font-size: 11.5px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin: 2px 0 0;
          }
          .sa-badge {
            position: relative;
            display: flex;
            align-items: center;
            gap: 6px;
            margin-top: 12px;
            padding: 6px 10px;
            border-radius: 8px;
            background: rgba(234, 53, 43, 0.15);
            border: 1px solid rgba(234, 53, 43, 0.35);
            color: #FCA5A5;
            font-size: 10.5px;
            font-weight: 700;
            letter-spacing: 0.1em;
          }
          .sa-dot {
            width: 3px;
            height: 3px;
            border-radius: 50%;
            background: #FCA5A5;
            opacity: 0.6;
          }
          .sa-access {
            font-weight: 500;
            letter-spacing: 0.02em;
            color: #D1D5DB;
          }

          .animate-fadeIn { animation: fadeIn 0.2s ease; }
          .animate-slideUp { animation: slideUp 0.25s cubic-bezier(0.34,1.56,0.64,1); }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      <Sidebar
        rootStyles={{
          [`.${sidebarClasses.container}`]: {
            backgroundColor: '#FFFFFF',
            width: '250px',
            borderRight: '1px solid #F1F1F1',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        className="h-screen sticky top-0 shadow-sm"
      >
        <div className="flex flex-col h-screen">
          {/* Logo Section */}
          <div className="px-6 pt-5 pb-4 border-b border-gray-100">
            <Img src={Logo} alt="Sidebar Logo" className="h-[2.40rem] object-contain" />
          </div>

          {/* Menu */}
          <div className="sb-scroll flex-1 pt-3">
            <p className="sb-section-label">MAIN MENU</p>
            <Menu
              className="sb-main"
              menuItemStyles={{
                button: ({ active, level }) => ({
                  backgroundColor: active && level === 0 ? '#FEF2F2' : 'transparent',
                  color: active ? BRAND : '#4B5563',
                  margin: level === 0 ? '2px 16px' : '0 12px 0 0',
                  borderRadius: '10px',
                  padding: level === 0 ? '10px 12px' : '8px 12px',
                  height: level === 0 ? '44px' : '38px',
                  fontSize: level === 0 ? '15px' : '14px',
                  fontWeight: active ? 600 : 500,
                  fontFamily: FONT,
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  '&:hover': {
                    backgroundColor: level === 0 ? (active ? '#FEF2F2' : '#F9FAFB') : 'transparent',
                    color: BRAND,
                  },
                }),
                subMenuContent: {
                  backgroundColor: 'transparent',
                },
                SubMenuExpandIcon: {
                  '& span': { width: '7px', height: '7px', borderRight: '2px solid', borderBottom: '2px solid' },
                },
              }}
            >
              {menuItems.map((item) => {
                if (item.isSubMenu) {
                  const isChildActive = item.children.some(child => location.pathname === child.path);
                  return (
                    <SubMenu
                      key={item.name}
                      label={item.name}
                      icon={item.icon}
                      active={isChildActive || location.pathname.startsWith(item.path)}
                      open={openSubMenu === item.name}
                      onOpenChange={(isOpen) => setOpenSubMenu(isOpen ? item.name : null)}
                    >
                      {item.children.map((child) => (
                        <MenuItem
                          key={child.name}
                          component={<NavLink to={child.path} />}
                          active={location.pathname === child.path}
                        >
                          {child.name}
                        </MenuItem>
                      ))}
                    </SubMenu>
                  );
                }
                return (
                  <MenuItem
                    key={item.name}
                    component={<NavLink to={item.path} />}
                    icon={item.icon}
                    active={location.pathname === item.path}
                  >
                    {item.name}
                  </MenuItem>
                );
              })}
            </Menu>
          </div>

          {/* Bottom: Logout + Super Admin card */}
          <div className="pt-3 pb-5  border-gray-100">
            <button className="sb-logout" onClick={() => setShowLogoutModal(true)}>
              <LogOut size={18} className="sb-logout-icon" />
              Logout
            </button>
            <SuperAdminCard />
          </div>
        </div>
      </Sidebar>

      <LogoutModal
        isOpen={showLogoutModal}
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutModal(false)}
      />
    </>
  );
}
